import {
  buildAncestorChain,
  buildAncestorPathLabel,
  buildChildrenByParent,
  buildHierarchyDisplayParts,
  buildOptionsByValue,
  hasChildren as nodeHasChildren,
  HierarchyDisplayParts,
  HierarchyTreeOption,
  resolveHierarchyLabel,
  resolveParentKey,
  ROOT_PARENT,
} from './hierarchyTree'

export interface IHierarchyAncestor {
  value: string
  label: string
}

export interface IHierarchyLeaf extends IHierarchyAncestor {
  pathLabel: string
}

// Accessors over a single Hierarchy Property's option set. Pure over the
// committed options — no record needed. Built once where the option set lives
// (the field catalog descriptor, or the display-value factory) and read by
// every consumer instead of re-deriving parent/label maps per surface.
export interface IHierarchyAccessors {
  // Parent value of a Hierarchy Option, or null when it has none. An orphan
  // parentValue (pointing at a missing option) normalises to null, so this
  // agrees with getAncestorChain/getPathLabel, which treat such a node as a root.
  getParentValue(value: string): string | null
  // The option's own short label ("Room 204"). Falls back to the raw value.
  getOwnLabel(value: string): string
  // The full breadcrumb label ("Room 204 Building A/Floor 2").
  getPathLabel(value: string): string
  // Root→leaf chain (own labels), cycle-guarded; empty when value is unknown.
  getAncestorChain(value: string): IHierarchyAncestor[]
  getLabelPath(value: string): string[]
  // Leaf label and ancestor path as separate pieces, so display surfaces can
  // style the breadcrumb (dimmed) without re-walking the tree.
  getDisplayParts(value: string): HierarchyDisplayParts
  // Direct children of a value (pass null for root options), in option order.
  getChildren(parentValue: string | null): IHierarchyAncestor[]
  // Whether a value has any children (cheap; precomputed).
  hasChildren(value: string): boolean
  // Every leaf option with its ancestor path label, for flat search lists.
  getLeaves(): IHierarchyLeaf[]
  // Expand deduped value entries into filter entries: each entry becomes its
  // root->leaf ancestor chain (path-labelled, deduped by value), falling back
  // to the entry itself when it has no ancestors.
  expandToFilterEntries<T extends { id: string; label: string }>(
    entries: ReadonlyArray<T>,
  ): Array<{ id: string; label: string }>
}

export const createHierarchyAccessors = (
  options: ReadonlyArray<HierarchyTreeOption>,
): IHierarchyAccessors => {
  const optionList = options as HierarchyTreeOption[]
  const optionsByValue = buildOptionsByValue(optionList)
  const childrenByParent = buildChildrenByParent(optionList, optionsByValue)

  return {
    getParentValue: value => {
      const option = optionsByValue.get(value)
      return option ? resolveParentKey(option, optionsByValue) : null
    },
    getOwnLabel: value => optionsByValue.get(value)?.label ?? value,
    getPathLabel: value => resolveHierarchyLabel(value, optionsByValue),
    getAncestorChain: value =>
      buildAncestorChain(value, optionsByValue).map(node => ({
        value: node.value,
        label: node.label,
      })),
    getLabelPath: value => {
      const chain = buildAncestorChain(value, optionsByValue)
      return chain.length
        ? chain.map(node => node.label)
        : [optionsByValue.get(value)?.label ?? value]
    },
    getDisplayParts: value => buildHierarchyDisplayParts(value, optionsByValue),
    getChildren: parentValue =>
      (childrenByParent.get(parentValue ?? ROOT_PARENT) || []).map(node => ({
        value: node.value,
        label: node.label,
      })),
    hasChildren: value => nodeHasChildren(value, childrenByParent),
    getLeaves: () =>
      optionList
        .filter(option => !nodeHasChildren(option.value, childrenByParent))
        .map(option => ({
          value: option.value,
          label: option.label,
          pathLabel: buildAncestorPathLabel(option.value, optionsByValue),
        })),
    expandToFilterEntries: entries =>
      Array.from(
        new Map(
          entries.flatMap(entry => {
            const ancestors = buildAncestorChain(entry.id, optionsByValue)
            if (!ancestors.length) {
              return [[entry.id, entry] as const]
            }
            return ancestors.map(
              node =>
                [
                  node.value,
                  {
                    id: node.value,
                    label: resolveHierarchyLabel(node.value, optionsByValue),
                  },
                ] as const,
            )
          }),
        ).values(),
      ),
  }
}

const accessorsByOptions = new WeakMap<object, IHierarchyAccessors>()

// Build-once entry point: memoizes accessors per options-array identity, so
// repeated reads of the same options array (within a memoized catalog/provider)
// reuse one instance instead of rebuilding the parent/child/label maps. Distinct
// array references still build their own instance. Use this everywhere; call
// createHierarchyAccessors directly only when you need a guaranteed fresh one.
export const getHierarchyAccessors = (
  options: ReadonlyArray<HierarchyTreeOption>,
): IHierarchyAccessors => {
  const cached = accessorsByOptions.get(options)
  if (cached) return cached

  const accessors = createHierarchyAccessors(options)
  accessorsByOptions.set(options, accessors)
  return accessors
}
